import { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import "./packages.css";
import { getProductList } from "../../API/mangoholidayAPI";
import { Helmet } from "react-helmet";

/* ------------------------------------------------------------------
    SEO Meta Config
-------------------------------------------------------------------*/
const TOUR_SEO = {
  europe: {
    title:
      "Europe Group Tours for Seniors | 55+ Escorted Europe Holidays | Mango Holidays",
    description:
      "Explore Europe with comfort & care. Mango Holidays offers 55+ senior citizen group tours across London, Paris, Switzerland, Italy & more — Indian meals, expert tour managers, premium hotels.",
  },
  japan: {
    title:
      "Japan Group Tours for Seniors | Cherry Blossom & Cultural Holidays | Mango Holidays",
    description:
      "Experience Japan’s charm with senior-friendly tours by Mango Holidays. Tokyo, Hiroshima, Osaka & more with Indian meals, comfort hotels, and caring tour managers.",
  },
  "south-america": {
    title:
      "South America Group Tours | Senior Citizen Escorted Holidays | Mango Holidays",
    description:
      "Discover South America with Mango Holidays — senior-friendly tours to Brazil, Argentina, Peru, Bolivia & Chile. Enjoy comfortable stays, expert tour leaders & authentic experiences.",
  },
  vietnam: {
    title:
      "Vietnam Group Tours for Seniors | Escorted Asia Holidays | Mango Holidays",
    description:
      "Travel through Vietnam with comfort & care. Explore Hanoi, Halong Bay, Da Nang & Ho Chi Minh City on senior-friendly group tours with Indian meals & caring tour managers.",
  },
  dubai: {
    title:
      "Dubai Group Tours for Seniors | Escorted Holidays to UAE | Mango Holidays",
    description:
      "Discover Dubai with Mango Holidays’ senior-friendly group tours. Visit Burj Khalifa, Desert Safari & more with Indian meals, comfortable hotels & expert tour escorts.",
  },
};

const Packages = () => {
  const { "tour-type": type, location, tourSlug } = useParams();

  const [tourLocation, setTourLocation] = useState(null);
  const [tourType, setTourType] = useState(null);
  const [tourPackages, setTourPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatTitle = (str) =>
    str
      ?.split("-")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ");

  useEffect(() => {
    setTourType(formatTitle(type));
    setTourLocation(formatTitle(location));
  }, [type, location]);

  const seo = TOUR_SEO[tourSlug] || {
    title: "Mango Holidays | Group Tours for Seniors",
    description:
      "Explore senior-friendly group tours across the world with Mango Holidays — Indian meals, comfort stays, and expert tour managers.",
  };

  useEffect(() => {
    document.title = `${tourLocation || ""} ${tourType || ""} | Mango Holidays`;
  }, [tourLocation, tourType]);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ------------------------------------------------------------------
      FETCH ALL TOURS & FILTER DYNAMICALLY
  -------------------------------------------------------------------*/
  useEffect(() => {
    async function fetchTours() {
      if (!location || !type) return;
      setLoading(true);

      try {
        const data = await getProductList({});
        if (!data?.ProductList) {
          setTourPackages([]);
          return;
        }

        console.log("All Sectors:", [
          ...new Set(data.ProductList.map((item) => item.SectorName)),
        ]);

        const today = new Date();
        const normalize = (str = "") =>
          str.toLowerCase().replace(/[\s--]/g, "");

        const locNorm = normalize(location);

        let filteredTours = data.ProductList.filter((item) =>
          normalize(item.SectorName || "").includes(locNorm),
        );

        const isCustom = type.toLowerCase().includes("custom");

        filteredTours = filteredTours.filter((tour) =>
          isCustom ? tour.TravelType === "FIT" : tour.TravelType === "GIT",
        );

        const toursWithPricing = filteredTours.map((item) => {
          let matchedNetINRValue = null;

          if (Array.isArray(item.ProductPricingHeader)) {
            for (const header of item.ProductPricingHeader) {
              const fromParts = header.TourDateFrom?.split("/") || [];
              const toParts = header.TourDateTo?.split("/") || [];

              const fromDate = new Date(
                `${fromParts[2]}-${fromParts[1]}-${fromParts[0]}`,
              );
              const toDate = new Date(
                `${toParts[2]}-${toParts[1]}-${toParts[0]}`,
              );

              if (today >= fromDate && today <= toDate) {
                matchedNetINRValue =
                  header?.ProductPricingDetail?.[0]?.NETINRValue ?? null;
                break;
              }
            }
          }

          return {
            ...item,
            NETINRValue: matchedNetINRValue,
          };
        });

        setTourPackages(toursWithPricing);
      } catch (err) {
        console.error("Error fetching tours:", err.message);
        setTourPackages([]);
      } finally {
        setLoading(false);
      }
    }

    fetchTours();
  }, [location, type]);

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
      </Helmet>
      {console.log(tourPackages)}
      {/* Banner */}
      <section className="banner-section-packages">
        <div className="img-container-packages">
          {tourPackages.length > 0 && (
            <img src={tourPackages[0].ProductImage} alt="Banner" />
          )}
        </div>
        <div className="banner-overlay-packages">
          <h1 className="banner-title-packages">
            {tourPackages[0]?.SectorName || ""}
          </h1>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="packages-cards-section">
        <div className="container">
          {loading ? (
            <div className="loader-container">
              <ClipLoader color="#ff5f10" size={100} />
              <p className="loading-text">Fetching tours...</p>
            </div>
          ) : tourPackages.length > 0 ? (
            <div className="packages-grid">
              {tourPackages.map((pkg) => (
                <div key={pkg.ProductCode} className="package-image-container">
                  <div className="package-image">
                    <img src={pkg.ProductImage} alt={pkg.ProductTitle} />
                  </div>

                  <div className="hover-overlay">
                    <i className="fas fa-link fa-2x"></i>
                  </div>

                  <div className="package-overlay-content">
                    <h3>{pkg.ProductTitle}</h3>
                    <p>
                      {pkg.Days} days | {pkg.Nights} nights
                    </p>
                    <div className="price-bar">
                      <h3>
                        {pkg.LowestTwinSharingPrice
                          ? `₹ ${Number(
                              pkg.LowestTwinSharingPrice,
                            ).toLocaleString("en-IN")}`
                          : pkg.NETINRValue
                            ? `₹ ${Number(pkg.NETINRValue).toLocaleString(
                                "en-IN",
                              )}`
                            : ""}
                      </h3>

                      <NavLink
                        to={`/tour-details/${pkg.ProductID}/${
                          pkg.ProductCode
                        }/${pkg.ProductTitle.toLowerCase().replace(
                          /\s+/g,
                          "-",
                        )}`}
                        state={{ productImg: pkg.ProductImage }}
                        onClick={handleScrollToTop}
                      >
                        <p>See Details</p>
                      </NavLink>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="data-unavailable-banner">
              <img src="/img/data-not-available.webp" alt="No Tours" />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Packages;
